// Popovers, menus, tooltips, and alerts — Chakra defaults use warm gray.800 in dark mode.

const overlaySurface = {
  bg: 'surface',
  color: 'text',
  borderWidth: '1px',
  borderColor: 'border',
  borderRadius: 'lg',
  boxShadow: 'lg',
}

export const menuTheme = {
  baseStyle: {
    list: {
      ...overlaySurface,
      py: 1,
      minW: '10rem',
    },
    item: {
      bg: 'transparent',
      color: 'text',
      // Background only — do not set color here so per-item tokens (e.g. text.danger) survive hover/focus.
      _hover: {
        bg: 'surface.muted',
      },
      _focus: {
        bg: 'surface.muted',
      },
      _disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    },
    itemOption: {
      bg: 'transparent',
      color: 'text',
      _hover: {
        bg: 'surface.muted',
      },
      _focus: {
        bg: 'surface.muted',
      },
      _checked: {
        bg: 'nav.active.bg',
        color: 'nav.active.text',
      },
    },
    divider: {
      borderColor: 'divider',
      my: 1,
    },
    groupTitle: {
      color: 'text.muted',
      fontSize: 'xs',
      fontWeight: 'semibold',
      textTransform: 'uppercase',
      letterSpacing: 'wider',
      px: 3,
      py: 2,
    },
    button: {
      color: 'text.secondary',
      _hover: { bg: 'surface.muted', color: 'accent.soft' },
      _expanded: { bg: 'surface.muted', color: 'accent.soft' },
    },
  },
}

export const popoverTheme = {
  baseStyle: {
    content: overlaySurface,
    header: {
      borderBottomWidth: '1px',
      borderColor: 'border',
      color: 'text',
    },
    body: {
      color: 'text',
    },
    footer: {
      borderTopWidth: '1px',
      borderColor: 'border',
    },
    arrow: {
      bg: 'surface',
    },
  },
}

export const tooltipTheme = {
  defaultProps: {
    hasArrow: false,
  },
  baseStyle: {
    bg: 'surface.muted',
    color: 'text',
    borderWidth: '1px',
    borderColor: 'border',
    borderRadius: 'md',
    px: 3,
    py: 2,
    boxShadow: 'md',
    fontSize: 'sm',
  },
}

export const alertTheme = {
  baseStyle: {
    container: {
      borderRadius: 'lg',
    },
    title: {
      color: 'text',
    },
    description: {
      color: 'text.secondary',
    },
  },
  variants: {
    subtle: (props: { colorScheme: string }) => ({
      container: {
        bg: `${props.colorScheme}.50`,
        _dark: {
          bg: 'surface.muted',
          borderWidth: '1px',
          borderColor: 'border',
        },
      },
    }),
    'left-accent': (props: { colorScheme: string }) => ({
      container: {
        bg: `${props.colorScheme}.50`,
        borderLeftColor: `${props.colorScheme}.500`,
        _dark: {
          bg: 'surface.muted',
          borderLeftColor: `${props.colorScheme}.400`,
        },
      },
    }),
    solid: (props: { colorScheme: string }) => ({
      container: {
        bg: `${props.colorScheme}.500`,
        color: 'white',
      },
      // baseStyle sets title/description to semantic text tokens; on solid status
      // backgrounds (toasts, etc.) those tokens fail WCAG — force high-contrast white.
      title: {
        color: 'white',
      },
      description: {
        color: 'white',
      },
    }),
  },
}
